import { useStoreState } from '@ariakit/react'
import { useCallback, useEffect } from 'react'

import { AttributeSection } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { useEmbroidery } from '../../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { useEmbroideryForm } from '../../../../../containers/EmbroideryContainer/useEmbroideryForm'
import { EmbroideryOptionWrapper, FormBody } from '../../../Modal/primitives/shared-form-styles'
import { SubmissionButtons } from '../../../Modal/primitives/SubmissionButtons/SubmissionButtons'
import { LogoPreviewItem } from '../PreviewItems/LogoPreviewItem'

import { PortalEmbroideryLogo } from './PortalEmbroideryLogo'
import { TeamStoreLogoPlacement } from './TeamStoreLogoPlacement'
import { useValidationSchema } from './useValidationSchema'

export const B2BLogoEmbroideryForm = () => {
  const {
    embroideryPriceRangeByType,
    productEmbroideryConfig,
    selectedFormKind,
    disabledFormFields,
    isTeamStoreEmbroidery,
    setCurrentPlacement,
    withPricing,
  } = useEmbroidery()

  const logoPlacements = productEmbroideryConfig?.options.logo?.placements
  const isLogoPlacementDisabled = disabledFormFields?.LOGO_B2B?.includes('logoPlacement') ?? false
  const showTeamStoreLogoPlacementPicker = Boolean(
    isTeamStoreEmbroidery && logoPlacements && logoPlacements.length > 0
  )

  const validationSchema = useValidationSchema()
  const { form, resetForm } = useEmbroideryForm('LOGO_B2B', {
    validationSchema,
    createInitialValues: values => ({
      ...values,
      // Legacy portals do not offer placement selection, so only team stores preselect one.
      logoPlacement:
        values.logoPlacement ??
        (isTeamStoreEmbroidery && !isLogoPlacementDisabled ? logoPlacements?.[0] : undefined),
    }),
    createEmbroideryOptions: values => ({
      logoName: values.logoName,
      logoImageSource: values.logoImageSource,
      logoPlacement: values.logoPlacement,
      // Team store: embroidery logo id → line-item `_logo_file_name`. Must forward or
      // interactive submit overwrites auto-select defaults and drops the identifier.
      logoDesignFile: values.logoDesignFile,
      // Not user-editable; must be forwarded so interactive submit doesn't drop it.
      manualHandling: values.manualHandling,
    }),
    previewItem: values => <LogoPreviewItem values={values} />,
  })

  const {
    embroidery: {
      modal: { forms: fixtures },
    },
  } = useTranslation()

  const logoImageSource = useStoreState(form, s => s.values.logoImageSource)
  const logoName = useStoreState(form, s => s.values.logoName)
  const logoPlacement = useStoreState(form, s => s.values.logoPlacement)

  useEffect(() => {
    if (!logoPlacement) return
    setCurrentPlacement(selectedFormKind, logoPlacement)
  }, [logoPlacement, selectedFormKind, setCurrentPlacement])

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      void form.submit()
    },
    [form]
  )

  return (
    <FormBody onSubmit={handleSubmit}>
      {showTeamStoreLogoPlacementPicker ? (
        <TeamStoreLogoPlacement
          labelId='logo-placement-for-b2b'
          placementOptions={logoPlacements}
          form={form}
          disabled={isLogoPlacementDisabled}
        />
      ) : logoImageSource ? (
        <EmbroideryOptionWrapper>
          <AttributeSection title={fixtures.logoOption.logoLabel}>
            <PortalEmbroideryLogo
              src={logoImageSource}
              alt={`${logoName} Embroidery Logo`}
              widths={{ unit: 'px', sm: 180, md: 180 }}
            />
          </AttributeSection>
        </EmbroideryOptionWrapper>
      ) : null}
      <EmbroideryOptionWrapper>
        <SubmissionButtons
          displayPrice={embroideryPriceRangeByType.LOGO?.effectivePrice.displayValue}
          withPricing={withPricing}
          onReset={() => {
            resetForm()
          }}
        />
      </EmbroideryOptionWrapper>
    </FormBody>
  )
}
